import React, { useState, useRef, useCallback, useEffect, Suspense } from 'react';
import { Provider as EventBus } from 'react-bus';
import { install } from 'resize-observer';

import '../App.css';

import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { PopupPropsContext } from "@progress/kendo-react-popup";

import store from './store';
import { Provider } from 'react-redux';
import { Provider as EventBusProvider } from 'react-bus'

import ProtectedRoute from './ProtectedRoute.jsx';

import PopupMessages from '../messages/PopupMessages.jsx';
import ConstituoView from 'views/constituo/ConstituoView.jsx';
import LoginView from 'views/login/LoginView.jsx';
import LoginCustomView from 'views/login/LoginCustomView.jsx';
import LoginMessageDialog from 'views/widgets/LoginMessageDialog.jsx';
import ConnectingView from "views/login/ConnectingView.jsx";
import LoadingPanel from "views/widgets/LoadingPanel.jsx";
import ErrorPanel from "views/widgets/ErrorPanel.jsx";

import useMutationObserver from '../hooks/useMutationObserver';
import useSession from '../hooks/useSession'; 

const SiteMapView = React.lazy(() => import('views/user/about/SiteMapView.jsx'));

const configurePopupAnimation = (popupProps) => ({
	...popupProps,
	animate: false
});

const UserView = React.lazy(() => import('views/user/UserView.jsx'));
const ProfileView = React.lazy(() => import('views/user/profile/ProfileView.jsx'));

const MessagesSearchView = React.lazy(() => import('views/user/messages/MessagesSearchView.jsx'));
const AboutView = React.lazy(() => import('views/user/about/AboutView.jsx'));

const DashboardView = React.lazy(() => import('views/dashboards/DashboardView.jsx'));
const DashboardSearchView = React.lazy(() => import('views/dashboards/DashboardSearchView.jsx'));
const DashboardAddModifyView = React.lazy(() => import('views/dashboards/DashboardAddModifyView.jsx'));
const DashboardViewView = React.lazy(() => import('views/dashboards/DashboardViewView.jsx'));

const WorkflowSearchView = React.lazy(() => import('views/admin/workflows/WorkflowSearchView.jsx'));
const WorkflowAddModifyView = React.lazy(() => import('views/admin/workflows/WorkflowAddModifyView.jsx'));
const WorkflowViewView = React.lazy(() => import('views/admin/workflows/WorkflowViewView.jsx'));

const QuerySearchView = React.lazy(() => import('views/admin/queries/QuerySearchView.jsx'));
const QueryAddModifyView = React.lazy(() => import('views/admin/queries/QueryAddModifyView.jsx'));
const QueryViewView = React.lazy(() => import('views/admin/queries/QueryViewView.jsx'));

const DatalistSearchView = React.lazy(() => import('views/admin/datalists/DatalistSearchView.jsx'));
const DatalistAddModifyView = React.lazy(() => import('views/admin/datalists/DatalistAddModifyView.jsx'));
const DatalistViewView = React.lazy(() => import('views/admin/datalists/DatalistViewView.jsx'));

const FileSourceSearchView = React.lazy(() => import('views/admin/filesources/FileSourceSearchView.jsx'));
const FileSourceAddModifyView = React.lazy(() => import('views/admin/filesources/FileSourceAddModifyView.jsx'));
const FileSourceViewView = React.lazy(() => import('views/admin/filesources/FileSourceViewView.jsx'));

const KeyFileSearchView = React.lazy(() => import('views/admin/keyfiles/KeyFileSearchView.jsx'));
const KeyFileAddModifyView = React.lazy(() => import('views/admin/keyfiles/KeyFileAddModifyView.jsx'));
const KeyFileViewView = React.lazy(() => import('views/admin/keyfiles/KeyFileViewView.jsx'));

const DatabaseSearchView = React.lazy(() => import('views/admin/databases/DatabaseSearchView.jsx'));
const DatabaseAddModifyView = React.lazy(() => import('views/admin/databases/DatabaseAddModifyView.jsx'));
const DatabaseViewView = React.lazy(() => import('views/admin/databases/DatabaseViewView.jsx'));

const StampSearchView = React.lazy(() => import('views/admin/stamps/StampSearchView.jsx'));
const StampAddModifyView = React.lazy(() => import('views/admin/stamps/StampAddModifyView.jsx'));
const StampViewView = React.lazy(() => import('views/admin/stamps/StampViewView.jsx'));

const ServiceGroupSearchView = React.lazy(() => import('views/admin/servicegroups/ServiceGroupSearchView.jsx'));
const ServiceGroupAddModifyView = React.lazy(() => import('views/admin/servicegroups/ServiceGroupAddModifyView.jsx'));
const ServiceGroupViewView = React.lazy(() => import('views/admin/servicegroups/ServiceGroupViewView.jsx'));

const CustomScriptSearchView = React.lazy(() => import('views/tools/customscripts/CustomScriptSearchView.jsx'));
const CustomScriptAddModifyView = React.lazy(() => import('views/tools/customscripts/CustomScriptAddModifyView.jsx'));
const CustomScriptViewView = React.lazy(() => import('views/tools/customscripts/CustomScriptViewView.jsx'));

const LocationImportSearchView = React.lazy(() => import('views/tools/locationimports/LocationImportSearchView.jsx'));
const LocationImportAddModifyView = React.lazy(() => import('views/tools/locationimports/LocationImportAddModifyView.jsx'));
const LocationImportViewView = React.lazy(() => import('views/tools/locationimports/LocationImportViewView.jsx'));

const EmailImportSearchView = React.lazy(() => import('views/tools/emailimports/EmailImportSearchView.jsx'));
const EmailImportAddModifyView = React.lazy(() => import('views/tools/emailimports/EmailImportAddModifyView.jsx'));
const EmailImportViewView = React.lazy(() => import('views/tools/emailimports/EmailImportViewView.jsx'));

const ApiImportSearchView = React.lazy(() => import('views/tools/apiimports/ApiImportSearchView.jsx'));
const ApiImportAddModifyView = React.lazy(() => import('views/tools/apiimports/ApiImportAddModifyView.jsx'));
const ApiImportViewView = React.lazy(() => import('views/tools/apiimports/ApiImportViewView.jsx'));

const MonitorView = React.lazy(() => import('views/controlpanel/monitor/MonitorView.jsx'));

const ThreadPoolSearchView = React.lazy(() => import('views/controlpanel/threadpools/ThreadPoolSearchView.jsx'));
const ThreadPoolAddModifyView = React.lazy(() => import('views/controlpanel/threadpools/ThreadPoolAddModifyView.jsx'));
const ThreadPoolViewView = React.lazy(() => import('views/controlpanel/threadpools/ThreadPoolViewView.jsx'));

const OrganizationView = React.lazy(() => import('views/system/organization/OrganizationView.jsx'));

const WorkspaceSearchView = React.lazy(() => import('views/system/workspaces/WorkspaceSearchView.jsx'));
const WorkspaceAddModifyView = React.lazy(() => import('views/system/workspaces/WorkspaceAddModifyView.jsx'));
const WorkspaceViewView = React.lazy(() => import('views/system/workspaces/WorkspaceViewView.jsx'));

const RoleSearchView = React.lazy(() => import('views/system/roles/RoleSearchView.jsx'));
const RoleAddModifyView = React.lazy(() => import('views/system/roles/RoleAddModifyView.jsx'));
const RoleViewView = React.lazy(() => import('views/system/roles/RoleViewView.jsx'));

const InstanceSearchView = React.lazy(() => import('views/system/instances/InstanceSearchView.jsx'));
const InstanceAddModifyView = React.lazy(() => import('views/system/instances/InstanceAddModifyView.jsx'));
const InstanceViewView = React.lazy(() => import('views/system/instances/InstanceViewView.jsx'));

const CategorySearchView = React.lazy(() => import('views/system/categories/CategorySearchView.jsx'));
const CategoryAddModifyView = React.lazy(() => import('views/system/categories/CategoryAddModifyView.jsx'));
const CategoryViewView = React.lazy(() => import('views/system/categories/CategoryViewView.jsx'));

const RoutineSearchView = React.lazy(() => import('views/controlpanel/routines/RoutineSearchView.jsx'));
const RoutineAddModifyView = React.lazy(() => import('views/controlpanel/routines/RoutineAddModifyView.jsx'));
const RoutineViewView = React.lazy(() => import('views/controlpanel/routines/RoutineViewView.jsx'));

const DocumentTypeSearchView = React.lazy(() => import('views/system/documenttypes/DocumentTypeSearchView.jsx'));
const DocumentTypeAddModifyView = React.lazy(() => import('views/system/documenttypes/DocumentTypeAddModifyView.jsx'));
const DocumentTypeViewView = React.lazy(() => import('views/system/documenttypes/DocumentTypeViewView.jsx'));

const AttributeSearchView = React.lazy(() => import('views/system/attributes/AttributeSearchView.jsx'));
const AttributeAddModifyView = React.lazy(() => import('views/system/attributes/AttributeAddModifyView.jsx'));
const AttributeViewView = React.lazy(() => import('views/system/attributes/AttributeViewView.jsx'));

const GroupSearchView = React.lazy(() => import('views/system/groups/GroupSearchView.jsx'));
const GroupAddModifyView = React.lazy(() => import('views/system/groups/GroupAddModifyView.jsx'));
const GroupViewView = React.lazy(() => import('views/system/groups/GroupViewView.jsx'));

const UserSearchView = React.lazy(() => import('views/system/users/UserSearchView.jsx'));
const UserAddModifyView = React.lazy(() => import('views/system/users/UserAddModifyView.jsx'));
const UserViewView = React.lazy(() => import('views/system/users/UserViewView.jsx'));

const SatelliteSearchView = React.lazy(() => import('views/system/satellites/SatelliteSearchView.jsx'));
const SatelliteAddModifyView = React.lazy(() => import('views/system/satellites/SatelliteAddModifyView.jsx'));
const SatelliteViewView = React.lazy(() => import('views/system/satellites/SatelliteViewView.jsx'));

if (!window.ResizeObserver) install();

export default function Constituo() {
	
	let [ verified, setVerified ] = useState(false);
	const [ messages, setMessages ] = useState([]);
	const [ timeoutDialog, setTimeoutDialog ] = useState(false);
	const [ connecting, setConnecting ] = useState(true);
	let { session, setSession, setLoggedOn, isAlreadyLoggedOn,
		  recordUserActivity, 
		  persistPreferences, setPreference,
		  messageInfo, getUserLoginMessage,
		setLoginMessageViewed } = useSession({timeoutCallback: setTimeoutDialog});
	const containerRef = useRef();
	const inactivityFocusRef = useRef();
	
	useEffect(() => {
		document.title = PRODUCT_NAME
		checkIfSessionExists();
	}, []);
	
	useEffect(() => {
		if (inactivityFocusRef.current) inactivityFocusRef.current.focus();
	}, [timeoutDialog]);
	
	const checkIfSessionExists = () => {
		setConnecting(true);
		fetch(API_URL + "/public/activeSessionExists", {
			method: 'GET',
			credentials: 'include'
		}).then(function(response) {
			if(response.status==428){
				return false;
			}else if (!response.ok) {
				throw Error(response.statusText);
			}
			if (response.redirected) // if redirecting, no session and no valid json, so just return false
				return false;
			else
				return response.json();
		}).then(function(hasActiveSession) {
			
			// if there is an active session, fetch it and skip over login
			if (hasActiveSession) {
				fetchCurrentSession();
				
			// else no active session, so delete all cookies and allow login
			} else {
				deleteCookie('AWSELB', '/');
				deleteCookie('AWSELBCORS', '/');
				deleteCookie('HASH_AWSELB', '/');
				deleteCookie('HASH_AWSELBCORS', '/');
				deleteCookie('AWSALB', '/');
				deleteCookie('AWSALBCORS', '/');
				deleteCookie('HASH_AWSALB', '/');
				deleteCookie('HASH_AWSALBCORS', '/');
				setConnecting(false);
			}
		
		// attempt to check session failed, so display error
		}).catch(function(error) {
			setConnecting(false);
			addMessage({ type: 'error', msg: error.message });
		});
	};
	
	// fetch current session
	const fetchCurrentSession = () => {
		fetch(API_URL + "/api/session/", {
			method: 'GET',
			credentials: 'include'
		}).then(function(response) {
			if (!response.ok) {
				throw Error(response.statusText);
			} 
			return response.json();
		}).then(function(session) {
			setSession(session);
			setVerified(true);
			setConnecting(false);
			if (!isAlreadyLoggedOn()) 
				setLoggedOn();
		}).catch(function(error) {
			setConnecting(false);
			addMessage({ type: 'error', msg: error.message });
		});
	};
	
	const deleteCookie = (name, path) => {
		if( getCookie( name ) ) {
			document.cookie = name + "=" + ((path) ? ";path="+path:"") + ";expires=Thu, 01 Jan 1970 00:00:01 GMT";
		}
	};

	const getCookie = (name) => {
		return document.cookie.split(';').some(c => {
			return c.trim().startsWith(name + '=');
		});
	};
		
	const loadPreferences = (authResponse) => {
		let preferences = authResponse.preferences;
		for (let i = 0; i < preferences.length; i++) {
			let pref = preferences[i];
			setPreference(pref.key, pref.value, authResponse.user);
		}
	};
	
	const addMessage = (msg) => {
		let newMsgs = messages;
		newMsgs.concat({
			...msg,
			msgIdx : newMsgs.length,
			time: 0			
		});
		setMessages(newMsgs);
	};
	
	const removeMessage = (msgIdx) => {
		let mArray = [];
		for (let i = 0; i < messages.length; i++) 
			if (i !== msgIdx) 
				mArray.push(messages[i]);
		setMessages(mArray);
	};
	
	const onElementMutated = useCallback((mutations) => {
		for (var x = 0; x < mutations.length; x++) {
			var mutation = mutations[x];
			var mutationClasses = mutation.target.className;
			var mutationAttributeName = mutation.attributeName;
					
			var validMutation = true;
			
			if(mutationAttributeName!=null && mutationAttributeName.includes('title')){
				validMutation = false;
			}
			// exclude kendo and flexlayout widgets from triggering session update
			if(mutationClasses!=null && typeof mutationClasses == 'string' 
				&& (mutationClasses.includes('progress-bar') 
					|| mutationClasses.includes('constituo-visually-hidden')
					|| mutationClasses.includes('constituo-multivalue-values-container')
					|| mutationClasses.includes('dot') 
					|| mutationClasses.includes('slide')
					|| mutationClasses.includes('k-')
					|| mutationClasses.includes('CodeMirror')
					|| mutationClasses.includes('flexlayout__layout'))){
				validMutation = false;
			}
			// exclude grid selector columns from triggering session update
			if(mutation.target.localName == 'col') {
				validMutation = false;
			}
			// exclude code mirror from triggering session update
			if(mutation.target.innerHTML.includes("CodeMirror")) {
				validMutation = false;
			}
			
			if(validMutation){
				recordUserActivity();
				break;
			}
		}	
	}, []);	
	useMutationObserver(containerRef.current, onElementMutated);
	
	if (connecting) 
		return ( 
			<div className="main-view" ref={containerRef}> 
				<ConnectingView />
			</div> 
		);
		
	else if (!session || !verified){
    	return (
			<div className="main-view" ref={containerRef}> 
				<Router>
					<Routes>
							<Route path="/sso/:orgCode" element={<LoginCustomView authenticatedCallback={checkIfSessionExists} 
									 							loadPreferences={loadPreferences}/>} />
							<Route path="/sso/:orgCode/autoStart/:autoStart" element={<LoginCustomView authenticatedCallback={checkIfSessionExists} 
									 							loadPreferences={loadPreferences}/>} />
							<Route path="/sso/:orgCode/idp/:idpInit" element={<LoginCustomView authenticatedCallback={checkIfSessionExists} 
									 							loadPreferences={loadPreferences}/>} />											 							
							<Route path="/*" element={<LoginView authenticatedCallback={checkIfSessionExists} 
									 							loadPreferences={loadPreferences}/>} />
					</Routes>
				</Router>
    		</div>
    	);
  	}
  	else { 
  		const loginMessage = getUserLoginMessage();
  		let defaultView = (session.instance ? (<EventBus><UserView /></EventBus>) : (<EventBus><OrganizationView /></EventBus>));

		return (
			<div 
				className="main-view"
				ref={containerRef}
				
				// globally prevent default right-click, disabling per CCM-2606
		        onContextMenu={(e) => {
        			//e.preventDefault(); 
      			}}				
			>
				<Suspense fallback={<LoadingPanel />}>
					<Router>
						<ErrorPanel redirect={true} >
							<Provider store={store}>
								<EventBusProvider>
									<PopupPropsContext.Provider value={configurePopupAnimation}>
									<ConstituoView 
										messageInfo={messageInfo}
									>
										<Routes>
											<Route path="/" element={defaultView} />
											<Route path="/user" element={<ProtectedRoute><EventBus><UserView /></EventBus></ProtectedRoute>} />
											<Route path="/profile" element={<ProfileView isEditable={false} />} />
											<Route path="/profile/modify" element={<ProfileView isEditable={true} />} />
											<Route path="/messages" element={<MessagesSearchView isEditable={false} />} />
											<Route path="/about" element={<AboutView okCallback={() => {}} />} />
											<Route path="/sitemap" element={<SiteMapView />} />							
										
											<Route path="/dashboards" element={<ProtectedRoute><DashboardView /></ProtectedRoute>} />
											<Route path="/dashboards/search" element={<ProtectedRoute><DashboardSearchView /></ProtectedRoute>} />
											<Route path="/dashboards/add" element={<ProtectedRoute><DashboardAddModifyView /></ProtectedRoute>} />
											<Route path="/dashboards/modify/:id" element={<ProtectedRoute><DashboardAddModifyView /></ProtectedRoute>} />
											<Route path="/dashboards/view/:id" element={<ProtectedRoute><DashboardViewView /></ProtectedRoute>} />
											
											<Route path="/workflows" element={<ProtectedRoute><WorkflowSearchView /></ProtectedRoute>} />
											<Route path="/workflows/add" element={<ProtectedRoute><WorkflowAddModifyView /></ProtectedRoute>} />
											<Route path="/workflows/modify/:id" element={<ProtectedRoute><WorkflowAddModifyView /></ProtectedRoute>} />
											<Route path="/workflows/view/:id" element={<ProtectedRoute><WorkflowViewView /></ProtectedRoute>} />
											
											<Route path="/queries" element={<ProtectedRoute><QuerySearchView /></ProtectedRoute>} />
											<Route path="/queries/add" element={<ProtectedRoute><QueryAddModifyView /></ProtectedRoute>} />
											<Route path="/queries/modify/:id" element={<ProtectedRoute><QueryAddModifyView /></ProtectedRoute>} />
											<Route path="/queries/view/:id" element={<ProtectedRoute><QueryViewView /></ProtectedRoute>} />
											
											<Route path="/datalists" element={<ProtectedRoute><DatalistSearchView /></ProtectedRoute>} />
											<Route path="/datalists/add" element={<ProtectedRoute><DatalistAddModifyView /></ProtectedRoute>} />
											<Route path="/datalists/modify/:id" element={<ProtectedRoute><DatalistAddModifyView /></ProtectedRoute>} />
											<Route path="/datalists/view/:id" element={<ProtectedRoute><DatalistViewView /></ProtectedRoute>} />											
											
											<Route path="/filesources" element={<ProtectedRoute><FileSourceSearchView /></ProtectedRoute>} />
											<Route path="/filesources/add" element={<ProtectedRoute><FileSourceAddModifyView /></ProtectedRoute>} />
											<Route path="/filesources/modify/:id" element={<ProtectedRoute><FileSourceAddModifyView /></ProtectedRoute>} />
											<Route path="/filesources/view/:id" element={<ProtectedRoute><FileSourceViewView /></ProtectedRoute>} />
											
											<Route path="/keyfiles" element={<ProtectedRoute><KeyFileSearchView /></ProtectedRoute>} />
											<Route path="/keyfiles/add" element={<ProtectedRoute><KeyFileAddModifyView /></ProtectedRoute>} />
											<Route path="/keyfiles/modify/:id" element={<ProtectedRoute><KeyFileAddModifyView /></ProtectedRoute>} />
											<Route path="/keyfiles/view/:id" element={<ProtectedRoute><KeyFileViewView /></ProtectedRoute>} />	
											
											<Route path="/databases" element={<ProtectedRoute><DatabaseSearchView /></ProtectedRoute>} />
											<Route path="/databases/add" element={<ProtectedRoute><DatabaseAddModifyView /></ProtectedRoute>} />
											<Route path="/databases/modify/:id" element={<ProtectedRoute><DatabaseAddModifyView /></ProtectedRoute>} />
											<Route path="/databases/view/:id" element={<ProtectedRoute><DatabaseViewView /></ProtectedRoute>} />
											
											<Route path="/stamps" element={<ProtectedRoute><StampSearchView /></ProtectedRoute>} />
											<Route path="/stamps/add" element={<ProtectedRoute><StampAddModifyView /></ProtectedRoute>} />
											<Route path="/stamps/modify/:id" element={<ProtectedRoute><StampAddModifyView /></ProtectedRoute>} />
											<Route path="/stamps/view/:id" element={<ProtectedRoute><StampViewView /></ProtectedRoute>} />
											
											<Route path="/servicegroups" element={<ProtectedRoute><ServiceGroupSearchView /></ProtectedRoute>} />
											<Route path="/servicegroups/add" element={<ProtectedRoute><ServiceGroupAddModifyView /></ProtectedRoute>} />
											<Route path="/servicegroups/modify/:id" element={<ProtectedRoute><ServiceGroupAddModifyView /></ProtectedRoute>} />
											<Route path="/servicegroups/view/:id" element={<ProtectedRoute><ServiceGroupViewView /></ProtectedRoute>} />	
											
											<Route path="/customscripts" element={<ProtectedRoute><CustomScriptSearchView /></ProtectedRoute>} />
											<Route path="/customscripts/add" element={<ProtectedRoute><CustomScriptAddModifyView /></ProtectedRoute>} />
											<Route path="/customscripts/modify/:id" element={<ProtectedRoute><CustomScriptAddModifyView /></ProtectedRoute>} />
											<Route path="/customscripts/view/:id" element={<ProtectedRoute><CustomScriptViewView /></ProtectedRoute>} />												

											<Route path="/locationimports" element={<ProtectedRoute><LocationImportSearchView /></ProtectedRoute>} />
											<Route path="/locationimports/add" element={<ProtectedRoute><LocationImportAddModifyView /></ProtectedRoute>} />
											<Route path="/locationimports/modify/:id" element={<ProtectedRoute><LocationImportAddModifyView /></ProtectedRoute>} />
											<Route path="/locationimports/view/:id" element={<ProtectedRoute><LocationImportViewView /></ProtectedRoute>} />												

											<Route path="/emailimports" element={<ProtectedRoute><EmailImportSearchView /></ProtectedRoute>} />
											<Route path="/emailimports/add" element={<ProtectedRoute><EmailImportAddModifyView /></ProtectedRoute>} />
											<Route path="/emailimports/modify/:id" element={<ProtectedRoute><EmailImportAddModifyView /></ProtectedRoute>} />
											<Route path="/emailimports/view/:id" element={<ProtectedRoute><EmailImportViewView /></ProtectedRoute>} />												

											<Route path="/apiimports" element={<ProtectedRoute><ApiImportSearchView /></ProtectedRoute>} />
											<Route path="/apiimports/add" element={<ProtectedRoute><ApiImportAddModifyView /></ProtectedRoute>} />
											<Route path="/apiimports/modify/:id" element={<ProtectedRoute><ApiImportAddModifyView /></ProtectedRoute>} />
											<Route path="/apiimports/view/:id" element={<ProtectedRoute><ApiImportViewView /></ProtectedRoute>} />												
											
											<Route path="/organization" element={<ProtectedRoute><OrganizationView /></ProtectedRoute>} />							
					
											<Route path="/workspaces" element={<ProtectedRoute><WorkspaceSearchView /></ProtectedRoute>} />
											<Route path="/workspaces/add" element={<ProtectedRoute><WorkspaceAddModifyView /></ProtectedRoute>} />
											<Route path="/workspaces/modify/:id" element={<ProtectedRoute><WorkspaceAddModifyView /></ProtectedRoute>} />
											<Route path="/workspaces/view/:id" element={<ProtectedRoute><WorkspaceViewView /></ProtectedRoute>} />
											
											<Route path="/workspace/modify/:id" element={<ProtectedRoute><WorkspaceAddModifyView /></ProtectedRoute>} />
											<Route path="/workspace/view/:id" element={<ProtectedRoute><WorkspaceViewView /></ProtectedRoute>} />											
											
											<Route path="/roles" element={<ProtectedRoute><RoleSearchView /></ProtectedRoute>} />
											<Route path="/roles/add" element={<ProtectedRoute><RoleAddModifyView /></ProtectedRoute>} />
											<Route path="/roles/modify/:id" element={<ProtectedRoute><RoleAddModifyView /></ProtectedRoute>} />
											<Route path="/roles/view/:id" element={<ProtectedRoute><RoleViewView /></ProtectedRoute>} />
											
											<Route path="/instances" element={<ProtectedRoute><InstanceSearchView /></ProtectedRoute>} />
											<Route path="/instances/add" element={<ProtectedRoute><InstanceAddModifyView /></ProtectedRoute>} />
											<Route path="/instances/modify/:id" element={<ProtectedRoute><InstanceAddModifyView /></ProtectedRoute>} />
											<Route path="/instances/view/:id" element={<ProtectedRoute><InstanceViewView /></ProtectedRoute>} />	
											
											<Route path="/categories" element={<ProtectedRoute><CategorySearchView /></ProtectedRoute>} />
											<Route path="/categories/add" element={<ProtectedRoute><CategoryAddModifyView /></ProtectedRoute>} />
											<Route path="/categories/modify/:id" element={<ProtectedRoute><CategoryAddModifyView /></ProtectedRoute>} />
											<Route path="/categories/view/:id" element={<ProtectedRoute><CategoryViewView /></ProtectedRoute>} />	
												
											<Route path="/documenttypes" element={<ProtectedRoute><DocumentTypeSearchView /></ProtectedRoute>} />
											<Route path="/documenttypes/add" element={<ProtectedRoute><DocumentTypeAddModifyView /></ProtectedRoute>} />
											<Route path="/documenttypes/modify/:id" element={<ProtectedRoute><DocumentTypeAddModifyView /></ProtectedRoute>} />
											<Route path="/documenttypes/view/:id" element={<ProtectedRoute><DocumentTypeViewView /></ProtectedRoute>} />								

											<Route path="/attributes" element={<ProtectedRoute><AttributeSearchView /></ProtectedRoute>} />
											<Route path="/attributes/add" element={<ProtectedRoute><AttributeAddModifyView /></ProtectedRoute>} />
											<Route path="/attributes/modify/:id" element={<ProtectedRoute><AttributeAddModifyView /></ProtectedRoute>} />
											<Route path="/attributes/view/:id" element={<ProtectedRoute><AttributeViewView /></ProtectedRoute>} />
																
											<Route path="/groups" element={<ProtectedRoute><GroupSearchView /></ProtectedRoute>} />
											<Route path="/groups/add" element={<ProtectedRoute><GroupAddModifyView /></ProtectedRoute>} />
											<Route path="/groups/modify/:id" element={<ProtectedRoute><GroupAddModifyView /></ProtectedRoute>} />
											<Route path="/groups/view/:id" element={<ProtectedRoute><GroupViewView /></ProtectedRoute>} />							
					
											<Route path="/users" element={<ProtectedRoute><UserSearchView /></ProtectedRoute>} />
											<Route path="/users/add" element={<ProtectedRoute><UserAddModifyView /></ProtectedRoute>} />
											<Route path="/users/modify/:id" element={<ProtectedRoute><UserAddModifyView /></ProtectedRoute>} />
											<Route path="/users/view/:id" element={<ProtectedRoute><UserViewView /></ProtectedRoute>} />
											
											<Route path="/satellites" element={<ProtectedRoute><SatelliteSearchView /></ProtectedRoute>} />
											<Route path="/satellites/add" element={<ProtectedRoute><SatelliteAddModifyView /></ProtectedRoute>} />
											<Route path="/satellites/modify/:id" element={<ProtectedRoute><SatelliteAddModifyView /></ProtectedRoute>} />
											<Route path="/satellites/view/:id" element={<ProtectedRoute><SatelliteViewView /></ProtectedRoute>} />
											
											<Route path="/monitor" element={<ProtectedRoute><MonitorView /></ProtectedRoute>} />

											<Route path="/threadpools" element={<ProtectedRoute><ThreadPoolSearchView /></ProtectedRoute>} />
											<Route path="/threadpools/add" element={<ProtectedRoute><ThreadPoolAddModifyView /></ProtectedRoute>} />
											<Route path="/threadpools/modify/:id" element={<ProtectedRoute><ThreadPoolAddModifyView /></ProtectedRoute>} />
											<Route path="/threadpools/view/:id" element={<ProtectedRoute><ThreadPoolViewView /></ProtectedRoute>} />
											
											<Route path="/routines" element={<ProtectedRoute><RoutineSearchView /></ProtectedRoute>} />											
											<Route path="/routines/add" element={<ProtectedRoute><RoutineAddModifyView /></ProtectedRoute>} />
											<Route path="/routines/modify/:id" element={<ProtectedRoute><RoutineAddModifyView /></ProtectedRoute>} />
											<Route path="/routines/view/:id" element={<ProtectedRoute><RoutineViewView /></ProtectedRoute>} />
												
											<Route path="*" element={<Navigate to="/" />} />
										</Routes>
									</ConstituoView>
									
									{(loginMessage != null
									  && loginMessage.trim().length > 0) && (
										<LoginMessageDialog
											title={"Login Message"}
											message={loginMessage}
											okCallback={() => { setLoginMessageViewed(); }}
										/>
									)}
								
					        		{ timeoutDialog && (
										<Dialog title={"Timeout Warning"} onClose={() => { recordUserActivity(); setTimeoutDialog(false); }}>
											<p
												style={{
													margin: "25px",
													textAlign: "center",
												}}
											>
												{"Your session is about to expire.  Would you like to keep working?"}
											</p>
											<DialogActionsBar>
												<button
													ref={inactivityFocusRef}
													className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
													onClick={() => { recordUserActivity(); setTimeoutDialog(false); }}
												>
													Yes
												</button>
												<button
													className="k-button k-button-md k-rounded-md k-button-solid k-button-olid-base"
													onClick={() => { persistPreferences(); }}
												>
													No
												</button>
											</DialogActionsBar>
										</Dialog>
									)}
									
									<PopupMessages messages={messages} removeMessage={removeMessage} />
									</PopupPropsContext.Provider>
								</EventBusProvider>
							</Provider>
						</ErrorPanel>
					</Router>
				</Suspense>
			</div>
		);
	}
}
